import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SanityImage } from 'sanity-image';
import { LocalizedLink } from '@rsc/gatsby-localization-helpers';
import breakpoints from '../../breakpoints';

const imageWidthCss = presentation => {
  switch (presentation) {
    case 'full-width':
    case 'content-width':
      return '100%';
    default:
      return 'auto';
  }
};

const StyledImage = styled(SanityImage)`
  display: block;
  margin: 0 auto;
  width: ${({ presentation }) => imageWidthCss(presentation)};
  max-width: 100%;
  margin-bottom: 1rem;
`;

const sanityBaseUrl = `https://cdn.sanity.io/images/${process.env.GATSBY_SANITY_PROJECT_ID}/${process.env.GATSBY_SANITY_DATASET}/`;

function BlockContentImage({ value }) {
  const { presentation, link } = value;

  let imageWidthPixels = presentation?.includes('full-width')
    ? 1920
    : parseInt(breakpoints.xl, 10);

  const originalWidth = value?.asset?.metadata?.dimensions?.width;
  if (imageWidthPixels > originalWidth) imageWidthPixels = originalWidth;

  if (value?.asset?._id && link?.linkType === 'external') {
    return (
      <a
        href={link.href}
        target={`${link.blank ? '_blank' : '_self'}`}
        rel="noopener noreferrer"
      >
        <StyledImage
          id={value.asset._id}
          baseUrl={sanityBaseUrl}
          alt={value.alt}
          loading="lazy"
          hotspot={value?.hotspot}
          crop={value?.crop}
          width={imageWidthPixels}
          presentation={presentation}
          sizes={`${imageWidthPixels}px`}
        />
      </a>
    );
  }

  if (value?.asset?._id && link?.linkType === 'internal') {
    return (
      <LocalizedLink to={link.href} rel="noopener noreferrer">
        <StyledImage
          id={value.asset._id}
          baseUrl={sanityBaseUrl}
          alt={value.alt}
          loading="lazy"
          hotspot={value?.hotspot}
          crop={value?.crop}
          width={imageWidthPixels}
          presentation={presentation}
          sizes={`${imageWidthPixels}px`}
        />
      </LocalizedLink>
    );
  }

  if (value?.asset?._id) {
    return (
      <StyledImage
        id={value.asset._id}
        baseUrl={sanityBaseUrl}
        alt={value.alt}
        loading="lazy"
        hotspot={value?.hotspot}
        crop={value?.crop}
        width={imageWidthPixels}
        presentation={presentation}
        sizes={`${imageWidthPixels}px`}
      />
    );
  }

  return null;
}

BlockContentImage.propTypes = {
  value: PropTypes.shape({
    asset: PropTypes.shape({
      _id: PropTypes.string,
      metadata: PropTypes.shape({
        dimensions: PropTypes.shape({
          width: PropTypes.number.isRequired,
        }),
      }),
    }),
    crop: PropTypes.arrayOf(PropTypes.shape({})),
    hotspot: PropTypes.arrayOf(PropTypes.shape({})),
    alt: PropTypes.string,
    presentation: PropTypes.string.isRequired,
    link: PropTypes.shape({
      href: PropTypes.string,
      linkType: PropTypes.string,
      blank: PropTypes.bool,
    }),
  }).isRequired,
};

export default BlockContentImage;
